/**
 * Listing Sort Utility
 *
 * Priority-based sorting for listings based on active attribute filters -
 * loops the shared attribute registry (Favorites, Alerts, New, Price
 * Change, Open House, in that priority order) instead of hand-checking
 * each attribute, so a listing's sort group and within-group order can
 * never drift from the same registry ListingsDrawer/Map.ts/CNTLDrawer use.
 * Listings matching no active attribute (or when none are active) sort by
 * price descending.
 */

import type { ListingSummary, AttributeFilters, AttributeIdSets } from '@slowmaui/shared-types';
import { factsFromListingSummary } from '@slowmaui/shared-types';
import { ATTRIBUTE_REGISTRY } from '@slowmaui/ui-components';

export interface SortOptions {
  attributes: AttributeFilters;
  favoriteIds?: Set<string>;
  alertIds?: Set<string>;
  agentPickIds?: Set<string>;
  featuredIds?: Set<string>;
}

/**
 * Sort listings by priority groups based on active attributes
 */
export function sortListings(
  listings: ListingSummary[],
  options: SortOptions
): ListingSummary[] {
  const { attributes, favoriteIds = new Set(), alertIds = new Set(), agentPickIds = new Set(), featuredIds = new Set() } = options;
  const idSets: AttributeIdSets = { favorites: favoriteIds, alerts: alertIds, agentPicks: agentPickIds, featured: featuredIds };

  // If no attributes are selected, sort by price descending
  if (!ATTRIBUTE_REGISTRY.some((entry) => attributes[entry.id])) {
    return [...listings].sort((a, b) => b.listPrice - a.listPrice);
  }

  // Each listing's group is the highest-priority active attribute it
  // matches (registry order = priority order); listings matching none fall
  // to the end, sorted by price like the no-attributes-active case above.
  const grouped = listings.map((listing) => {
    const facts = factsFromListingSummary(listing);
    const entry = ATTRIBUTE_REGISTRY.find((e) => attributes[e.id] && e.matches(facts, idSets));
    return { listing, entry };
  });

  return grouped
    .sort((a, b) => {
      const priorityA = a.entry?.priority ?? ATTRIBUTE_REGISTRY.length;
      const priorityB = b.entry?.priority ?? ATTRIBUTE_REGISTRY.length;
      if (priorityA !== priorityB) return priorityA - priorityB;

      // Within the same group, use that attribute's own recency sort
      // (Favorites/Alerts have none, so they - and the unmatched group -
      // fall through to price descending).
      if (a.entry?.sortCompare) return a.entry.sortCompare(a.listing, b.listing);
      return b.listing.listPrice - a.listing.listPrice;
    })
    .map(({ listing }) => listing);
}
